// src/PythonConsole.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { Button, Toast, List as MList } from 'antd-mobile';
import { Spinner } from 'react-bootstrap';
import { List, Drawer, Tour } from 'antd';
import Editor from '@monaco-editor/react';
import { FaAngleRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

import Header from './visual/header';
import { examplesData } from '../data/examples';
import { getWeekNumber } from '../services/utils';


function getPyFilename(filename) {
  // Validate the input to ensure it is a string
  if (typeof filename !== 'string') {
    throw new Error('Invalid input: filename must be a string');
  }

  // Use a regular expression to remove the leading number and underscore
  const transformedFilename = filename.replace(/^\d+_/, '');

  // Replace hyphens with underscores
  const finalFilename = transformedFilename.replace(/-/g, '_');

  // Append the .py extension
  return `${finalFilename}.py`;
}

function PythonConsole() {
  const { filename } = useParams();
  const [pyodide, setPyodide] = useState(null);
  const [code, setCode] = useState('print("¡Hola, LittleCoder!")');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [codeComment, setCodeComment] = useState('Uso de la la función print() para mostrar un mensaje en pantalla.');
  const [tourOpen, setTourOpen] = useState(false);

  // Refs para los 4 pasos del tour
  const refEjercicios = useRef(null);
  const refEjecutar = useRef(null);
  const refEditor = useRef(null);
  const refSalida = useRef(null);

  const TOUR_KEY = 'lc_playground_tour_shown';

  const tourSteps = [
    {
      title: '📚 Ejercicios de la Semana',
      description: 'Aquí encontrarás todos los ejercicios de Python disponibles. Los del futuro están bloqueados; se van desbloqueando semana a semana.',
      target: () => refEjercicios.current,
    },
    {
      title: '▶️ Botón de Ejecutar',
      description: 'Cuando hayas escrito o modificado tu código, pulsa este botón para ejecutarlo. El resultado aparecerá abajo en la sección de Salida.',
      target: () => refEjecutar.current,
    },
    {
      title: '💻 Editor de Código',
      description: 'Este es tu editor de Python. Puedes escribir y modificar el código libremente. ¡Es igual que Visual Studio Code!',
      target: () => refEditor.current,
    },
    {
      title: '🟠 Salida del Programa',
      description: 'Aquí aparecerá el resultado del ejercicio Python tras pulsar Ejecutar. Si hay errores, también los verás aquí.',
      target: () => refSalida.current,
    },
  ];

  let navigate = useNavigate();
  const weekIndex = getWeekNumber(new Date());

  useEffect(() => {
    const loadPyodide = async () => {
      try {
        const pyodide = await window.loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/',
        });
        setPyodide(pyodide);
      } catch (error) {
        console.error('Error al cargar Pyodide:', error);
      } finally {
        setLoading(false);
        // Mostrar el tour la primera vez que se accede al playground
        if (!localStorage.getItem('lc_playground_tour_shown')) {
          setTimeout(() => setTourOpen(true), 800);
        }
      }
    };

    if (!window.loadPyodide) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/pyodide.js';
      script.onload = loadPyodide;
      document.body.appendChild(script);
    } else {
      loadPyodide();
    }

    console.log("week", getWeekNumber(new Date()))
  }, []);

  useEffect(() => {
    if (filename) {
      const fetchCode = async () => {
        try {
          //TODO: resolverlo mejor en un subdir, pero no funciona por permisos o CORS
          const response = await fetch(`/${filename}.json`);
          if (!response.ok) {
            throw new Error('No se pudo cargar el código');
          }
          const data = await response.json();
          setCode('"""\n' + data.description + '\n"""\n\n' + data.code + '\n');
          setCodeComment(data.description);
        } catch (error) {
          console.error('Error al cargar el código:', error);
          setCode('# Error al cargar el código. Prueba con tu propio codigo y aprende.');
        }
      };
      fetchCode();
    }
  }, [filename]);

  const runCode = async () => {
    if (!pyodide) return;

    try {
      setOutput('');
      Toast.show({
        icon: 'loading',
        content: 'Ejecutando código...',
        duration: 0,
      });

      await pyodide.runPythonAsync(`
import sys
from io import StringIO
sys.stdout = sys.stderr = StringIO()
      `);

      await pyodide.runPythonAsync(code);

      const stdout = await pyodide.runPythonAsync('sys.stdout.getvalue()');
      setOutput(stdout);
    } catch (err) {
      setOutput(err.toString());
    } finally {
      Toast.clear();
    }
  };


  // Manejador de selección del árbol
  const onSelect = (keys, info) => {
    if (info.node.url) {
      setIsDrawerOpen(false); // Cierra el drawer en móvil tras seleccionar
      navigate("/playground/" + info.node.url.split('/').pop());  // "2_variables");
    }
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href={(filename ? "/playground/" + filename : "/playground/")} />
        <meta name="description" content={filename ? getPyFilename(filename) + " :" + codeComment : ""} />
        <title>littlecoders.dev playground: {filename ? getPyFilename(filename) : "littlecoders.py"}</title>
      </Helmet>

      <Header />

      {/* Full-screen IDE layout: sidebar + editor, fills viewport below header */}
      <div style={{ display: "flex", height: "calc(100vh - 56px)", overflow: "hidden", backgroundColor: "#1e1e1e" }}>

        {/* Mobile: Ejercicios button */}
        <div className="d-md-none" style={{ position: "absolute", top: "64px", left: "10px", zIndex: 100 }}>
          <Button type="primary" onClick={() => setIsDrawerOpen(true)}>
            Ejercicios <FaAngleRight />
          </Button>
        </div>

        {/* Sidebar desktop */}
        <nav
          ref={refEjercicios}
          className="d-none d-md-flex flex-column bg-light"
          style={{ width: "240px", minWidth: "240px", overflowY: "auto", borderRight: "1px solid #dee2e6" }}
        >
          <div className="pt-3 px-3">
            <h5>Ejercicios</h5>
            <List
              itemLayout="horizontal"
              dataSource={examplesData}
              renderItem={(item) => (
                <List.Item style={{ padding: "4px 0" }}>
                  <List.Item.Meta
                    title={
                      item.id > weekIndex ? (
                        <span className='disabled'>{item.id}. {item.title}</span>
                      ) : (
                        <Link to={"/playground/" + item.key}>{item.id}. {item.title}</Link>
                      )
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        </nav>

        {/* Drawer para móvil */}
        <Drawer
          title="Ejercicios"
          placement="left"
          onClose={() => setIsDrawerOpen(false)}
          visible={isDrawerOpen}
          bodyStyle={{ padding: "10px", width: "100vw" }}
        >
          <MList>
            {examplesData.map(item => (
              <List.Item key={item.id} prefix={<i className="fab fa-python"></i>}>
                {
                  item.id > weekIndex ? (
                    <span className='disabled'>{item.id}. {item.title}</span>
                  ) : (
                    <Link to={"/playground/" + item.key} onClick={() => setIsDrawerOpen(false)}>
                      {item.id}. {item.title}
                    </Link>
                  )
                }
              </List.Item>
            ))}
          </MList>
        </Drawer>

        {/* Main editor area - grows to fill remaining width */}
        <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#1e1e1e" }}>

          {/* Tab Bar */}
          <div style={{ backgroundColor: "#2d2d2d", borderBottom: "1px solid #3c3c3c", padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ color: "#d4d4d4", fontFamily: "monospace", fontSize: "0.9rem" }}>
              <i className="fab fa-python me-2" style={{ color: "#569cd6" }}></i>
              {filename ? getPyFilename(filename) : "littlecoders.py"}
            </span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                title="Ayuda y tour de la pantalla"
                onClick={() => setTourOpen(true)}
                style={{ backgroundColor: "transparent", border: "1px solid #858585", borderRadius: "4px", color: "#858585", padding: "4px 10px", cursor: "pointer", fontSize: "0.85rem" }}
              >
                <i className="fa-solid fa-circle-question me-1"></i>Ayuda
              </button>
              <div ref={refEjecutar}>
                <Button
                  color="primary"
                  onClick={runCode}
                  style={{ backgroundColor: "#4F46E5", border: "none", borderRadius: "4px", fontWeight: "bold" }}
                >
                  <i className="fas fa-play me-1"></i> Ejecutar
                </Button>
              </div>
            </div>
          </div>

          {/* Editor - grows to take available space */}
          <div ref={refEditor} style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#d4d4d4" }}>
                <Spinner animation="border" variant="light" />
                <p className="mt-3" style={{ color: "#858585" }}>Cargando Python... esto puede tardar unos segundos.</p>
              </div>
            ) : (
              <Editor
                height="100%"
                defaultLanguage="python"
                value={code}
                onChange={(value) => setCode(value)}
                theme="vs-dark"
                options={{
                  fontSize: 14,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontFamily: "'Cascadia Code', 'Fira Code', monospace",
                  fontLigatures: true,
                }}
              />
            )}
          </div>

          {/* Output area - fixed height at bottom */}
          <div ref={refSalida} style={{ flexShrink: 0, borderTop: "1px solid #3c3c3c", padding: "10px 16px", backgroundColor: "#1e1e1e", maxHeight: "28vh", overflowY: "auto" }}>
            <span style={{ color: "#6a9955", fontFamily: "monospace", fontSize: "0.8rem", marginBottom: "5px", display: "block" }}>// SALIDA:</span>
            <pre style={{ color: "#ce9178", fontFamily: "monospace", fontSize: "0.9rem", margin: 0, minHeight: "40px", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "none", padding: 0 }}>
              {output || <span style={{ color: "#858585" }}>La salida del programa aparecerá aquí...</span>}
            </pre>
          </div>

        </main>
      </div>

      <Tour
        open={tourOpen}
        onClose={() => {
          setTourOpen(false);
          localStorage.setItem(TOUR_KEY, 'true');
        }}
        steps={tourSteps}
      />
    </>
  );
}

export default PythonConsole;
